import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { useDocumentTitle } from '../../lib/useBranding';
import { TopBar } from './TopBar';
import { ImpersonationBanner } from './ImpersonationBanner';
import { AnnouncementBanner } from '../../features/announcements/AnnouncementBanner';

export function AppShell() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setMobileOpen(false), [location.pathname]);
  useDocumentTitle();
  // Sidebar width is a PREFERENCE, not a place — a stated exception to the
  // every-view-has-a-URL rule (a shared link must not impose my chrome on you).
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem('sidebarCollapsed') === '1';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem('sidebarCollapsed', collapsed ? '1' : '0');
    } catch {
      /* fine */
    }
  }, [collapsed]);

  return (
    /* h-screen + overflow-hidden, then ONE scroll container in <main> — the
       digitaladmin lesson: a definite height is what makes flex-1 mean anything,
       and it is what prevents double scrollbars. */
    <div className="flex h-screen overflow-hidden">
      {/* Step 141 — on a phone the sidebar is a drawer: hidden until the top
          bar's menu button opens it, closed by the backdrop or by navigating.
          On md+ it is the fixed rail it always was. */}
      <Sidebar
        collapsed={collapsed}
        onToggle={() => setCollapsed((c) => !c)}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />
      {/*
        `min-w-0` is load-bearing, not tidying.

        A flex child defaults to `min-width: auto` — it refuses to shrink below
        its content. Without this, any page with a wide row (a ticket's
        content column plus its w-72 properties rail, a long activity line)
        pushes THE WHOLE SHELL wider than the window: the header stretches,
        the right rail slides off screen, and the page grows a horizontal
        scrollbar it should never have. Kai hit it on a ticket's Activity tab
        (2026-09-08) — *"the UI is weird, it extends the length of the page
        massively"* — but it was never that tab's fault. `truncate` and
        `overflow-hidden` deeper in the tree cannot help, because the
        container they would clip against is the thing being stretched.
      */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {/* Step 99 — above everything, because it changes the meaning of
            every screen below it. */}
        <ImpersonationBanner />
        <TopBar onMenu={() => setMobileOpen(true)} />
        {/* Step 51 — above everything: an announcement you have to scroll to
            is one that gets raised as a ticket anyway. */}
        <AnnouncementBanner />
        {/* `relative` so a full-height page can pin itself to this box with
            `absolute inset-0` instead of relying on `h-full` resolving through
            a scrolling ancestor. The queue does exactly that: it owns its own
            scrolling, and a percentage height that quietly resolves to `auto`
            is how it ended up not scrolling at all. */}
        <main id="main" tabIndex={-1} className="relative min-h-0 flex-1 overflow-y-auto focus:outline-none">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
